import React, {
 useState
} from 'react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 ArrowLeft, Sparkles, Loader2
} from 'lucide-react';
import {
    WorkflowStep, FetchStepConfig, ApiCallStepConfig, MutateStepConfig,
} from '../types/workflow.types';

interface AIGenerateWorkflowProps {
    onBack: () => void;
    onResult: (data: { name: string; description: string; steps: WorkflowStep[] }) => void;
}

const examplePrompts = [
    'Fetch users and send to external API',
    'Get products, filter by stock, update prices',
    'Query orders and insert into analytics',
];

let stepIdCounter = 0;
function nextStepId(): string {
    stepIdCounter += 1;
    return `step_${Date.now()}_${stepIdCounter}`;
}

function makeFetchStep(name: string, schema: string, fields: string[]): WorkflowStep {
    const config: FetchStepConfig = {
        schemaName: schema,
        fields,
        filters: [],
        limit: 100,
        orderBy: '',
        orderDirection: 'asc',
    };
    return {
 id: nextStepId(),
type: 'FETCH',
name,
config
};
}

function makeApiCallStep(name: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string): WorkflowStep {
    const config: ApiCallStepConfig = {
        connectionId: '',
        method,
        path,
        headers: {},
        body: '',
    };
    return {
 id: nextStepId(),
type: 'API_CALL',
name,
config
};
}

function makeMutateStep(name: string, operation: 'INSERT' | 'UPDATE' | 'DELETE', schema: string): WorkflowStep {
    const config: MutateStepConfig = {
        operation,
        schemaName: schema,
        fieldMappings: [],
        condition: '',
    };
    return {
 id: nextStepId(),
type: 'MUTATE',
name,
config
};
}

function mockAIWorkflowGeneration(text: string): { name: string; description: string; steps: WorkflowStep[] } {
    const lower = text.toLowerCase();

    if ((lower.includes('user') || lower.includes('profile')) && lower.includes('api')) {
        return {
            name: 'Sync Users to API',
            description: 'Fetches user data from the database and sends it to an external API endpoint.',
            steps: [
                makeFetchStep('Fetch Users', 'users', ['id', 'username', 'email', 'role']),
                makeApiCallStep('Send to External API', 'POST', '/api/v1/users/sync'),
            ],
        };
    }

    if ((lower.includes('product') || lower.includes('catalog')) && lower.includes('update')) {
        return {
            name: 'Update Product Prices',
            description: 'Fetches products filtered by stock status and updates their prices.',
            steps: [
                makeFetchStep('Fetch Products', 'products', ['id', 'name', 'price', 'stock_quantity']),
                makeMutateStep('Update Prices', 'UPDATE', 'products'),
            ],
        };
    }

    if ((lower.includes('order') || lower.includes('tracking')) && lower.includes('insert')) {
        return {
            name: 'Orders to Analytics',
            description: 'Queries order data and inserts it into the analytics schema.',
            steps: [
                makeFetchStep('Fetch Orders', 'orders', ['id', 'customer_id', 'status', 'total_amount', 'ordered_at']),
                makeMutateStep('Insert into Analytics', 'INSERT', 'analytics'),
            ],
        };
    }

    // Default: 3-step pipeline
    return {
        name: 'Data Processing Pipeline',
        description: 'A general data pipeline that fetches data, processes it via an API, and stores results.',
        steps: [
            makeFetchStep('Fetch Data', 'my_table', ['id', 'name', 'status']),
            makeApiCallStep('Process via API', 'POST', '/api/v1/process'),
            makeMutateStep('Store Results', 'INSERT', 'results'),
        ],
    };
}

const AIGenerateWorkflow: React.FC<AIGenerateWorkflowProps> = ({
 onBack, onResult
}) => {
    const [description, setDescription] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const handleGenerate = () => {
        if (!description.trim()) return;
        setIsProcessing(true);
        setTimeout(() => {
            const result = mockAIWorkflowGeneration(description);
            setIsProcessing(false);
            onResult(result);
        }, 2000);
    };

    return (
        <div className="flex flex-col gap-8 max-w-3xl mx-auto py-8">
            <div>
                <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <div className="flex items-center gap-2">
                    <Sparkles className="h-6 w-6" />
                    <h2 className="text-2xl font-bold">Generate Workflow with AI</h2>
                </div>
                <p className="text-muted-foreground mt-1">
                    Describe what your workflow should do, and we'll generate the steps for you.
                </p>
            </div>

            <div className="space-y-4">
                <Textarea
                    placeholder="Describe your workflow... e.g., 'Fetch all active users and send their data to an external CRM API'"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    className="resize-none"
                />

                <div className="flex flex-wrap gap-2">
                    <p className="text-sm text-muted-foreground w-full">Try an example:</p>
                    {examplePrompts.map((prompt) => (
                        <Badge
                            key={prompt}
                            variant="outline"
                            className="cursor-pointer hover:bg-muted transition-colors py-1.5 px-3"
                            onClick={() => setDescription(prompt)}
                        >
                            {prompt}
                        </Badge>
                    ))}
                </div>
            </div>

            <Button
                onClick={handleGenerate}
                disabled={!description.trim() || isProcessing}
                className="w-full"
                size="lg"
            >
                {isProcessing ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating workflow...
                    </>
                ) : (
                    <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate Workflow
                    </>
                )}
            </Button>
        </div>
    );
};

export default AIGenerateWorkflow;
